import { useTranslation } from 'react-i18next';
import type { ScientificObjectRef } from '../../../scientific-objects/ScientificObjectRef';
import type { ScientificObjectSummary } from '../../../scientific-objects/ScientificObjectSummary';
import type { ExternalGeneIdentifierNamespace, Gene } from './Gene';
import { getGene } from './geneApi';
import { RelationshipList } from '../../../knowledge/relationships/RelationshipList';
import { useAsyncResource } from '../../../hooks/useAsyncResource';

interface GeneWorkspaceProps {
  readonly reference: ScientificObjectRef;
  readonly summary: ScientificObjectSummary;
}

const IDENTIFIER_LABELS: Record<ExternalGeneIdentifierNamespace, string> = {
  NCBI_GENE: 'NCBI Gene',
  ENSEMBL_GENE: 'Ensembl Gene',
  HGNC: 'HGNC'
};
const STRAND_LABELS = { FORWARD: 'Forward strand', REVERSE: 'Reverse strand', UNKNOWN: 'Unknown strand' } as const;

export function GeneWorkspace({ reference, summary }: GeneWorkspaceProps) {
  const { t } = useTranslation();
  const state = useAsyncResource(() => getGene(reference.id), [reference.id], 'Unable to load this gene.');

  return (
    <main className="gene-workspace" data-object-id={reference.id}>
      <header className="gene-heading">
        <p className="eyebrow">{t('geneWorkspace.eyebrow')}</p>
        <h1>{summary.displayName}</h1>
        <p className="gene-id">{reference.id}</p>
      </header>
      {state.status === 'loading' ? <p>{t('geneWorkspace.loading')}</p> : null}
      {state.status === 'error' ? <p role="alert">{state.message}</p> : null}
      {state.status === 'ready' ? <GeneDetails gene={state.value} /> : null}
      <RelationshipList reference={reference} direction="outgoing" relationshipType="ENCODES" />
      <RelationshipList reference={reference} direction="outgoing" relationshipType="HAS_TRANSCRIPT" />
    </main>
  );
}

function GeneDetails({ gene }: { readonly gene: Gene }) {
  const { t } = useTranslation();
  return (
    <div className="gene-details">
      <section aria-labelledby="gene-overview-heading">
        <h2 id="gene-overview-heading">{t('geneWorkspace.overview')}</h2>
        <dl className="gene-facts">
          <div>
            <dt>{t('geneWorkspace.preferredSymbol')}</dt>
            <dd>{gene.preferredSymbol}</dd>
          </div>
          <div>
            <dt>{t('geneWorkspace.preferredName')}</dt>
            <dd>{gene.preferredName}</dd>
          </div>
        </dl>
      </section>
      <section aria-labelledby="gene-organism-heading">
        <h2 id="gene-organism-heading">{t('geneWorkspace.organism')}</h2>
        <dl className="gene-facts">
          <div>
            <dt>{t('geneWorkspace.scientificName')}</dt>
            <dd>
              <i>{gene.organism.scientificName}</i>
            </dd>
          </div>
          <div>
            <dt>{t('geneWorkspace.taxonomyId')}</dt>
            <dd>{gene.organism.taxonomyId}</dd>
          </div>
        </dl>
      </section>
      <section aria-labelledby="gene-identifiers-heading">
        <h2 id="gene-identifiers-heading">{t('geneWorkspace.identifiers')}</h2>
        {gene.externalIdentifiers.length === 0 ? (
          <p>{t('geneWorkspace.noIdentifiers')}</p>
        ) : (
          <dl className="gene-identifiers">
            {gene.externalIdentifiers.map((identifier) => (
              <div key={`${identifier.namespace}:${identifier.value}`}>
                <dt>{IDENTIFIER_LABELS[identifier.namespace]}</dt>
                <dd>{identifier.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
      <section aria-labelledby="gene-loci-heading">
        <h2 id="gene-loci-heading">Genomic loci</h2>
        {gene.loci.length === 0 ? (
          <p>No genomic loci recorded.</p>
        ) : (
          <div className="gene-loci">
            {gene.loci.map((locus) => (
              <article
                key={`${locus.assembly.identifier}:${locus.referenceSequence.identifier}:${locus.start}:${locus.end}:${locus.strand}`}
              >
                <h3>{locus.assembly.displayName}</h3>
                <dl className="gene-facts">
                  <div>
                    <dt>Assembly accession</dt>
                    <dd>{locus.assembly.identifier}</dd>
                  </div>
                  <div>
                    <dt>Reference sequence</dt>
                    <dd>
                      {locus.referenceSequence.displayName} <small>{locus.referenceSequence.identifier}</small>
                    </dd>
                  </div>
                  <div>
                    <dt>Location</dt>
                    <dd>
                      {locus.start}–{locus.end} <small>0-based, half-open [start, end)</small>
                    </dd>
                  </div>
                  <div>
                    <dt>Strand</dt>
                    <dd>{STRAND_LABELS[locus.strand]}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
